// @ts-check

import { useState, useEffect } from 'react';
import { useNotificationProvider } from 'hooks';
import { initProfileEditorUser, initProfileEditorValid } from '../data/profileEditorData';
import convertEditorUser from '../libs/convertEditorUser';
import handleProfileEditorUser from '../libs/handleProfileEditorUser';
import validProfileEditorUser from '../libs/validProfileEditorUser';
import USER from '../ts/profileEditorEnum';
import prepareUserProfileUpdate from '../libs/prepareUserProfileUpdate';
import { updateUserApi, uploadFile } from 'api';
import { NOTIF_USER_UPDATE_SUCCESS } from 'constants/notificationMsg';
import { storeUserInfoImage } from 'redux/userInfo/userInfoSlice';
import { useDispatch } from 'react-redux';

/**
 * @function useProfileEditor
 * @param {ProfileViewerState} state
 * @param {()=> void} onCanEdit
 * @param {()=> Promise<void>} onSetUser
 * @returns {Object}
 */
function useProfileEditor(state, onCanEdit, onSetUser) {
	const dispatch = useDispatch();

	const { notifyError, notifySuccess } = useNotificationProvider();

	const [loading, setLoading] = useState(false);
	const [canNotSave, setCanNotSave] = useState(false);
	const [editorUser, setEditorUser] = useState(initProfileEditorUser);
	const [initUser, setInitUser] = useState(initProfileEditorUser);
	const [valid, setValid] = useState(initProfileEditorValid);

	/**
	 * @function onValid
	 * @param {string} name
	 * @param {boolean} hasError
	 * @param {string} errorMessage
	 * @returns {void}
	 */
	const onValid = (name, hasError, errorMessage) => {
		setValid((prev) => ({
			...prev,
			[name]: { hasErr: hasError, msg: errorMessage, canNotSave: hasError },
		}));
	};

	/**
	 * @function onHandle
	 * @param {string} name
	 * @param {*} payload
	 * @returns {void}
	 */
	const onHandle = (name, payload) => {
		// @ts-ignore
		handleProfileEditorUser(name, payload, editorUser, setEditorUser);
		validProfileEditorUser(
			name,
			payload,
			editorUser[USER.PASSWORD],
			editorUser[USER.CONFIRM_PASSWORD],
			onValid,
		);
	};

	/**
	 * @async
	 * @function onUpdate
	 * @returns {Promise<void>}
	 */
	const onUpdate = async () => {
		let fileId = editorUser[USER.V_FILE_ID];

		try {
			setLoading(true);

			if (editorUser[USER.FILE] !== null) {
				const res = await uploadFile(editorUser[USER.FILE]);
				if (res.Success) {
					fileId = res.Payload.Id;
				}
			}

			const user = prepareUserProfileUpdate(editorUser, fileId);
			await updateUserApi(user);
			dispatch(storeUserInfoImage({ image: editorUser[USER.IMAGE], vFile: fileId }));
			await onSetUser();
			onCanEdit();
			notifySuccess(NOTIF_USER_UPDATE_SUCCESS);
		} catch (error) {
			notifyError(error.message);
		} finally {
			setLoading(false);
		}
	};

	/**
	 * @function onReset
	 * @returns {void}
	 */
	const onReset = () => {
		setEditorUser(initUser);
		setValid(initProfileEditorValid);
	};

	useEffect(() => {
		const _canNotSave = Object.values(valid).some(({ canNotSave }) => canNotSave === true);
		setCanNotSave(_canNotSave);
	}, [valid]);

	useEffect(() => {
		const convertedUser = convertEditorUser(state);
		setEditorUser(convertedUser);
		setInitUser(convertedUser);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [state.user]);

	return { loading, canNotSave, editorUser, valid, onHandle, onUpdate, onReset };
}

export default useProfileEditor;
